import React, { useState, useEffect, useMemo } from 'react';
import { Spinner, Button } from 'react-bootstrap';
import { Modal, ModalHeader } from '../../../globalComponents/ui/Modal';
import Checkbox from '../../../globalComponents/ui/Checkbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { getRoleCapabilities, getCapabilities, addCapabilitiesToRole, getSystemRolePermissions, updateRole } from '../services/authorizationService';
import styled from 'styled-components';

// MODULE_SCHEMA is now built dynamically from DB capabilities — see buildDynamicSchema below

const ACTIONS = ['view', 'create', 'update', 'delete'];
const ACTION_LABELS = { view: 'View', create: 'Create', update: 'Update', delete: 'Delete' };
const ACTION_COLUMN_MAP = {
  view: 'view',
  create: 'create',
  upload: 'create',
  assign: 'create',
  execute: 'create',
  send: 'create',
  update: 'update',
  reassign: 'update',
  delete: 'delete',
  remove: 'delete'
};

const MODULE_LABEL_MAP = {
  DATA_MANAGEMENT: 'Data Management',
  CARBON_SEQUESTRATION: 'Carbon Sequestration',
  DASHBOARD: 'Dashboard',
};

// ─── Build dynamic schema from API-fetched capabilities ────────────────────────
const buildDynamicSchema = (caps) => {
  const moduleMap = {};
  for (const cap of caps) {
    const key = cap.moduleKey || 'OTHER';
    if (!moduleMap[key]) moduleMap[key] = new Set();
    // resource = everything before the last dot e.g. "dashboard.view" → "dashboard"
    const parts = cap.name.split('.');
    parts.pop(); // remove action
    const resource = parts.join('.');
    if (resource) moduleMap[key].add(resource);
  }
  return Object.entries(moduleMap).map(([moduleKey, resSet]) => ({
    moduleKey,
    label: MODULE_LABEL_MAP[moduleKey] || moduleKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    resources: Array.from(resSet).sort(),
  }));
};

const prettyResource = (name) =>
  name.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

// ─────────────────────────────────────────────────────────────────────────────
// Styled components
// ─────────────────────────────────────────────────────────────────────────────

const Body = styled.div`
  padding: 20px 24px 16px;
  overflow-y: auto;
  max-height: calc(90vh - 70px);
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid #e2eaf0;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #1a2e38;
  outline: none;
  transition: border-color 0.2s;
  margin-bottom: 16px;
  &:focus { border-color: #3f88a5; box-shadow: 0 0 0 3px rgba(63,136,165,0.12); }
`;



const ActivePill = styled.span`
  background: #dcfce7;
  color: #166534;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 20px;
`;

const TableWrap = styled.div`
  border: 1px solid #e2eaf0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  max-height: 45vh;
  overflow-y: auto;
  position: relative;
  background: #fff;
`;

const GridHeader = styled.div`
  display: grid;
  grid-template-columns: 40% 15% 15% 15% 15%;
  background: #f8fafc;
  padding: 12px 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #4a5568;
  border-bottom: 2px solid #e2eaf0;
  position: sticky;
  top: 0;
  z-index: 10;
  align-items: center;
`;

const HeaderCol = styled.div`
  &.center {
    text-align: center;
    display: flex;
    justify-content: center;
  }
`;

const AccordionItem = styled.div`
  border-bottom: 1px solid #e2eaf0;
  &:last-child {
    border-bottom: none;
  }
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: #f8fafc;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #475569;
  transition: all 0.15s ease;
  user-select: none;
  border-bottom: 1px solid #edf2f7;
  
  position: sticky;
  top: 41px;
  z-index: 9;
  
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const CountBadge = styled.span`
  background: #f1f5f9;
  color: #475569;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
  text-transform: none;
  letter-spacing: normal;
  border: 1px solid #e2e8f0;
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 40% 15% 15% 15% 15%;
  padding: 12px 20px;
  border-bottom: 1px solid #f1f5f9;
  align-items: center;
  transition: background 0.12s;
  &:hover {
    background: #f8fafc;
  }
  &:last-child {
    border-bottom: none;
  }
`;

const RowCol = styled.div`
  font-size: 0.875rem;
  color: #334155;
  &.center {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
  }
`;

const ResourceName = styled.span`
  font-weight: 500;
  color: #1e293b;
  margin-left: 8px;
`;

const EmptyMsg = styled.div`
  text-align: center;
  padding: 40px 24px;
  color: #94a3b8;
  font-size: 0.9rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #edf2f7;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1.5px solid #e2eaf0;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #1a2e38;
  outline: none;
  width: 260px;
  transition: border-color 0.2s;
  &:focus { border-color: #3f88a5; }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  border-bottom: 1px solid #edf2f7;
  padding-bottom: 8px;
`;

const TabButton = styled.button`
  background: ${props => props.$active ? '#f0f7fa' : 'transparent'};
  border: 1px solid ${props => props.$active ? '#d0e6ef' : 'transparent'};
  color: ${props => props.$active ? '#3f88a5' : '#64748b'};
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
  
  &:hover {
    background: ${props => props.$active ? '#f0f7fa' : '#f8fafc'};
    color: ${props => props.$active ? '#3f88a5' : '#334155'};
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const RolePermissionModal = ({ show, onHide, role, onSaveSuccess }) => {
  const [allCaps, setAllCaps] = useState([]);
  const [selectedCaps, setSelectedCaps] = useState(new Set());
  const [systemRolePermissions, setSystemRolePermissions] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [roleName, setRoleName] = useState('');
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'selected'



  const validCaps = useMemo(() => allCaps.map(c => c.name), [allCaps]);

  // Build dynamic schema whenever allCaps changes
  const dynamicSchema = useMemo(() => buildDynamicSchema(allCaps), [allCaps]);

  useEffect(() => {
    if (show && role?.id) {
      setIsLoading(true);
      setActiveTab('all');

      // Fetch all system capabilities, role specific capabilities, and dynamic system role default permissions from registry
      Promise.all([
        getCapabilities(),
        getRoleCapabilities(role.id),
        getSystemRolePermissions()
      ])
        .then(([allData, roleData, systemPerms]) => {
          setAllCaps(allData || []);
          setSystemRolePermissions(systemPerms || {});
          setRoleName(role?.displayName || role?.name || '');
          const schema = buildDynamicSchema(allData || []);
          setExpandedModules(new Set(schema.map(m => m.moduleKey)));
          const currentValidCaps = (allData || []).map(c => c.name);

          if (role?.name === 'Admin') {
            // Admin gets ALL capabilities
            setSelectedCaps(new Set(currentValidCaps));
          } else if (systemPerms && systemPerms[role?.name]) {
            // Predefined system role — use the list from backend registry
            const allowed = systemPerms[role.name];
            // If wildcard, select all
            if (allowed.includes('*')) {
              setSelectedCaps(new Set(currentValidCaps));
            } else {
              setSelectedCaps(new Set(allowed.filter(p => currentValidCaps.includes(p))));
            }
          } else {
            // Custom role — load from database
            const grantedNames = (roleData || []).map((c) => c.name);
            setSelectedCaps(new Set(grantedNames));
          }
        })
        .catch((e) => {
          console.error("Error fetching capabilities", e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setSelectedCaps(new Set());
    }
  }, [show, role]);

  const isSystemRole = Object.keys(systemRolePermissions).includes(role?.name);

  // Toggle a single capability
  const handleToggleCap = (capName) => {
    if (isSystemRole) return;
    const newSet = new Set(selectedCaps);
    if (newSet.has(capName)) {
      newSet.delete(capName);
    } else {
      newSet.add(capName);
    }
    setSelectedCaps(newSet);
  };

  // Global select all
  const isAllSelected = validCaps.length > 0 && validCaps.every((c) => selectedCaps.has(c));
  const handleToggleAll = () => {
    if (isSystemRole) return;
    if (isAllSelected) {
      setSelectedCaps(new Set()); // clear all
    } else {
      setSelectedCaps(new Set(validCaps)); // select all
    }
  };

  // Module select all
  const getModCaps = (mod) => {
    const caps = [];
    for (const res of mod.resources) {
      const matched = allCaps.filter(c => {
        const parts = c.name.split('.');
        parts.pop();
        const r = parts.join('.');
        return r === res;
      });
      caps.push(...matched.map(c => c.name));
    }
    return caps;
  };

  const filteredSchema = useMemo(() => {
    const schema = dynamicSchema;
    if (!search.trim()) return schema;
    const q = search.toLowerCase();
    return schema.map(mod => {
      const isModMatch = mod.label.toLowerCase().includes(q);
      const filteredRes = mod.resources.filter(res =>
        prettyResource(res).toLowerCase().includes(q) || isModMatch
      );
      return { ...mod, resources: filteredRes };
    }).filter(mod => mod.resources.length > 0);
  }, [search, dynamicSchema]);

  const displayedSchema = useMemo(() => {
    if (activeTab === 'all') {
      return filteredSchema;
    }
    return filteredSchema
      .map(mod => {
        const filteredResources = mod.resources.filter(resource => {
          return allCaps.some(cap => {
            const parts = cap.name.split('.');
            const res = parts.slice(0, -1).join('.');
            return res === resource && selectedCaps.has(cap.name);
          });
        });
        if (filteredResources.length === 0) return null;
        return {
          ...mod,
          resources: filteredResources
        };
      })
      .filter(Boolean);
  }, [activeTab, filteredSchema, allCaps, selectedCaps]);

  const handleToggleModule = (mod) => {
    if (isSystemRole) return;
    const modCaps = getModCaps(mod);
    const isModSelected = modCaps.every((c) => selectedCaps.has(c));
    const newSet = new Set(selectedCaps);

    if (isModSelected) {
      // Uncheck all in this module
      modCaps.forEach(c => newSet.delete(c));
    } else {
      // Check all in this module
      modCaps.forEach(c => newSet.add(c));
    }
    setSelectedCaps(newSet);
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      alert("Role name is required.");
      return;
    }

    setIsSaving(true);
    try {
      const originalName = role?.displayName || role?.name || '';
      if (roleName.trim() !== originalName) {
        const payload = {};
        if (isSystemRole) {
          payload.displayName = roleName.trim();
        } else {
          payload.name = roleName.trim();
        }
        await updateRole(role.id, payload);
      }

      if (!isSystemRole) {
        const selectedIds = allCaps
          .filter((c) => selectedCaps.has(c.name))
          .map((c) => c.id);

        await addCapabilitiesToRole({
          roleId: role.id,
          capabilityIds: selectedIds,
        });
      }
      
      onSaveSuccess?.();
      onHide();
    } catch (e) {
      console.error("Failed to save role updates", e);
      alert(e?.message || "Failed to save changes.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onClose={onHide} maxWidth={860}>
      <ModalHeader
        title={`Manage Role — ${role?.displayName || role?.name || ''}`}
        subtitle={isSystemRole ? 'System predefined role · View default permissions and edit display name' : 'Manage role name and capabilities'}
        onClose={onHide}
      />

      <Body>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '18px', paddingBottom: '14px', borderBottom: '1px solid #edf2f7', gap: '24px' }}>
          <div style={{ flex: 1, maxWidth: '400px' }}>
            <FormLabel style={{ marginBottom: '4px' }}>Role Name *</FormLabel>
            <FormInput
              placeholder="Role Name"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              disabled={isSaving}
              style={{ marginBottom: 0 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <SearchInput
              placeholder="Search permissions…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <ActivePill>● Active</ActivePill>
          </div>
        </div>

        <TabContainer>
          <TabButton
            $active={activeTab === 'all'}
            onClick={() => setActiveTab('all')}
            type="button"
          >
            All Permissions
          </TabButton>
          <TabButton
            $active={activeTab === 'selected'}
            onClick={() => setActiveTab('selected')}
            type="button"
          >
            Selected Permissions ({selectedCaps.size})
          </TabButton>
        </TabContainer>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '48px 24px' }}>
            <Spinner animation="border" style={{ color: '#3f88a5' }} />
            <p style={{ marginTop: 12, color: '#6b8794', fontSize: '0.875rem' }}>
              Loading permissions…
            </p>
          </div>
        ) : (
          <TableWrap>
            <GridHeader>
              <HeaderCol>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {!isSystemRole && (
                    <Checkbox
                      checked={isAllSelected}
                      onChange={handleToggleAll}
                      disabled={isSystemRole}
                    />
                  )}
                  Resource / Feature
                </div>
              </HeaderCol>
              {ACTIONS.map((a) => (
                <HeaderCol key={a} className="center">{ACTION_LABELS[a]}</HeaderCol>
              ))}
            </GridHeader>

            {displayedSchema.length === 0 ? (
              <EmptyMsg>
                {activeTab === 'selected'
                  ? (search ? "No selected permissions match your search." : "No permissions selected for this role yet.")
                  : "No permissions match your search."}
              </EmptyMsg>
            ) : (
              displayedSchema.map((mod) => {
                const modCaps = getModCaps(mod);
                const isModSelected = modCaps.length > 0 && modCaps.every((c) => selectedCaps.has(c));
                const isExpanded = expandedModules.has(mod.moduleKey);
                const selectedCount = modCaps.filter(c => selectedCaps.has(c)).length;
                const totalCount = modCaps.length;

                return (
                  <AccordionItem key={mod.moduleKey}>
                    <AccordionHeader onClick={() => {
                      const newSet = new Set(expandedModules);
                      if (newSet.has(mod.moduleKey)) {
                        newSet.delete(mod.moduleKey);
                      } else {
                        newSet.add(mod.moduleKey);
                      }
                      setExpandedModules(newSet);
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FontAwesomeIcon
                          icon={isExpanded ? faChevronDown : faChevronRight}
                          style={{ color: '#64748b', fontSize: '0.8rem', width: '12px' }}
                        />
                        {!isSystemRole && (
                          <Checkbox
                            checked={isModSelected}
                            onChange={() => handleToggleModule(mod)}
                            disabled={isSystemRole}
                          />
                        )}
                        <span>{mod.label}</span>
                      </div>
                      
                      <CountBadge>
                        {selectedCount} / {totalCount} Selected
                      </CountBadge>
                    </AccordionHeader>

                    {isExpanded && (
                      <div style={{ background: '#fff' }}>
                        {mod.resources.map((resource) => (
                          <GridRow key={resource}>
                            <RowCol>
                              <div style={{ paddingLeft: '26px' }}>
                                <ResourceName>{prettyResource(resource)}</ResourceName>
                              </div>
                            </RowCol>
                            {ACTIONS.map((actionColumn) => {
                              const matchingCaps = allCaps.filter(c => {
                                const parts = c.name.split('.');
                                const act = parts.pop();
                                const res = parts.join('.');
                                return res === resource && (ACTION_COLUMN_MAP[act] || act) === actionColumn;
                              });

                              const visibleCaps = activeTab === 'selected'
                                ? matchingCaps.filter(c => selectedCaps.has(c.name))
                                : matchingCaps;

                              return (
                                <RowCol key={actionColumn} className="center">
                                  {visibleCaps.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                      {visibleCaps.map(cap => {
                                        const parts = cap.name.split('.');
                                        const act = parts.pop();
                                        const isGranted = selectedCaps.has(cap.name);
                                        return (
                                          <div key={cap.name} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Checkbox
                                              checked={isGranted}
                                              onChange={() => handleToggleCap(cap.name)}
                                              disabled={isSystemRole}
                                            />
                                            {visibleCaps.length > 1 && (
                                              <span style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase', fontFamily: 'monospace', fontWeight: 600 }}>
                                                {act}
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div style={{ color: '#cbd5e1', fontSize: '0.8rem', userSelect: 'none' }}>—</div>
                                  )}
                                </RowCol>
                              );
                            })}
                          </GridRow>
                        ))}
                      </div>
                    )}
                  </AccordionItem>
                );
              })
            )}
          </TableWrap>
        )}

        {!isLoading && (
          <Footer>
            <Button variant="outline-secondary" onClick={onHide} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              style={{ backgroundColor: '#3f88a5', borderColor: '#3f88a5' }}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <><Spinner animation="border" size="sm" className="me-2" /> Saving...</>
              ) : 'Save Changes'}
            </Button>
          </Footer>
        )}
      </Body>
    </Modal>
  );
};

export default RolePermissionModal;
